import React from "react";
import ContentSlider from "@qlik/qlik/src/components/ContentSlider";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import RichText from "@qlik/qlik/src/components/RichText";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import * as styles from "./styles.module.css";

/**
 * QuoteSlider component
 * @param {Object} data - The data for the slider
 * @returns {JSX.Element} - Rendered QuoteSlider component
 */
const QuoteSlider = ({ data }) => {
    const { title, description, body, mediaAsset = [], additionalConfiguration, customId } = data ?? {};
    // Slider settings
    const settings = {
        slidesToShow: 1,
        responsive: [
            {
                breakpoint: 992,
                settings: {
                    dots: true,
                    arrows: false,
                },
            },
        ],
    };

    const spacingClassNames = getSpacingClassName(additionalConfiguration, "py-15 md:py-30");

    return (
        <section className={`${spacingClassNames}`} id={customId}>
            <div
                role="region"
                aria-label="Carousel"
                aria-live="polite"
                className="relative slick-arrow-bottom-middle slick-slider-dots quote-slider"
            >
                <div className="container">
                    <div className="row">
                        {/* Render title if it exists */}
                        {title && (
                            <h2 className="mb-5 text-6xl font-semibold leading-tight tracking-tight10 text-blue">
                                {title}
                            </h2>
                        )}
                        {/* Render description if it exists */}
                        {description?.description && (
                            <p className="mb-7.5 text-2xl font-light leading-relaxed tracking-tight6 text-gray">
                                {description.description}
                            </p>
                        )}
                        <div className="mb-20">
                            <ContentSlider
                                settings={settings}
                                className={`flex bg-blue h-full  rounded-sm p-5 md:p-10 bg-no-repeat bg-right qlikSlider ${styles.background}`}
                                style={{
                                    backgroundImage:
                                        mediaAsset?.length > 0 ? `url(${mediaAsset?.[0]?.secure_url})` : "none",
                                }}
                            >
                                {/* Render each card item */}
                                {data.cardItems?.map((slide) => (
                                    <div key={slide.id} className={` ${styles.wrapper}`}>
                                        <div
                                            className={`flex flex-col-reverse md:flex-row ${
                                                slide?.media?.[0] ? "md:gap-16 xl:gap-33.75" : ""
                                            }`}
                                        >
                                            <div className="flex flex-col h-full md:justify-between min-h-[251px] gap-7.5 md:gap-15">
                                                <div className="justify-between">
                                                    {/* Render slide body if it exists */}
                                                    {slide.body?.raw && (
                                                        <blockquote
                                                            className={`text-white font-semibold indent-1.25 ${
                                                                styles.description
                                                            } ${
                                                                slide?.media?.[0] ? " md:max-w-88 xl:max-w-[630px]" : ""
                                                            }`}
                                                        >
                                                            {documentToReactComponents(JSON.parse(slide.body.raw))}
                                                        </blockquote>
                                                    )}
                                                </div>

                                                <div className="flex flex-col">
                                                    {/* Render slide title if it exists */}
                                                    {slide.title && (
                                                        <span className="text-white  text:base md:text-xl font-semibold leading-tight  tracking-normal mb-1.25">
                                                            {slide.title}
                                                        </span>
                                                    )}
                                                    {/* Render slide subtitle if it exists */}
                                                    {slide.subtitle && (
                                                        <div className="text-white">
                                                            <span className="text-white text-xs md:text-sm font-normal">
                                                                {slide.subtitle}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {slide?.cta && (
                                                        <div className="flex gap-3.75 flex-wrap max-w-92.5 mt-5.25 md:max-w-125 lg:max-w-155">
                                                            {slide?.cta?.map((button) => (
                                                                <Cta key={button.id} data={button} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {slide?.media?.[0] && (
                                                <div className="flex justify-center	md:justify-center  md:h-auto  items-center">
                                                    <Media
                                                        media={slide?.media?.[0]}
                                                        lazyLoad={false}
                                                        className="self-center w-full h-full object-scale-down max-w-80 md:object-contain"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </ContentSlider>
                        </div>

                        {/* Render body if it exists */}
                        {body && <RichText data={data} className={`md:mt-25 ${styles.bodyContent}`} />}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QuoteSlider;
